import React, {useEffect, useMemo, useState} from "react";
import {Col, Row} from "reactstrap";
import {formatMoney} from "../../../../../../core/services/utils/utils";
import {OutlinedDropdownForm} from "../../../../base/dropdown/outlined_dropdown";
import {OutlinedInputForm} from "../../../../base/input/outlined_input";
import {CheckboxForm} from "../../../../base/checkbox/checkbox";
import {Fade} from "@mui/material";
import Form from "../../../../base/form";
import ValidateMessages from "../../../../../../core/models/static/texts/vallidate-messages";
import Api from "../../../../../../core/services/api/api";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import IconButton from "../../../../base/button/icon_button";
import {CopyAll} from "@mui/icons-material";
import {MuiRFF, Yup} from '../../../../../components/form'
import DateUtils from "../../../../../../core/services/date-utils";


const schema = Yup.object().shape({
    amount: Yup.number().nullable().required(ValidateMessages.required)
        .typeError(ValidateMessages.incorrectType('Amount'))
        .min(0.01, ValidateMessages.greaterThan('0')),
    manual: Yup.boolean().nullable().default(false),
    reference: Yup.string().nullable().when('manual', {
        is: true,
        then: Yup.string().nullable().required(ValidateMessages.required),
        otherwise: Yup.string().nullable(),
    }),
    date: Yup.mixed()
        .nullable()
        .validDate(ValidateMessages.incorrect),
    paymentMethod: Yup.object().nullable().required(ValidateMessages.required),
});

const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);

const PayCustomerOrderOtherPaymentsSection = ({
                                                  pay,
                                                  formId,
                                                  remainingBalance,
                                                  minChargeAmount,
                                              }) => {
    const [otherPaymentMethods, setOtherPaymentMethods] = useState([]);
    const isMounted = useIsMounted();


    const initialValues = useMemo(() => ({
        date: DateUtils.UTCNow(),
    }), [])

    /**
     * as soon as the component mounts:
     * - fetches the list of other payments from the server.
     */
    useEffect(() => {
        getOtherPayments().then();
    }, [])

    /**
     * Fetches the list of other payments from the server and sets the internal state.
     */
    const getOtherPayments = async () => {
        const response = await Api.getAllOtherPaymentMethods();
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setOtherPaymentMethods(response?.data ?? []);
        }
    }

    /**
     * If checked, then sets the manual to true, otherwise removes the reference from form.
     * @param e
     * @param form
     */
    const onManualReferenceChange = (e, form) => {
        if (!e.target.checked) {
            form.change('reference', null)
        }
        form.change(e.target.name, e.target.checked)
    }

    return (
        <>
            <Form
                id={formId}
                className='w-100'
                onSubmit={pay}
                validate={validate}
                initialValues={initialValues}
                render={({values, form}) => {
                    return <Row
                        className='no-gutters justify-content-center align-items-center w-100 mt-4'>
                        <Col xs={12}>
                            <div className='text-color-3 text-start text-sm mb-4 mx-4 d-inline-block'>
                                You can choose the amount of this payment. Your total remaining balance is
                                <span className={'ml-2 font-weight-semi-bold text-md black'}>
                                    {formatMoney(remainingBalance)}
                                </span>
                                <span className={'ml-2'}>
                                   <IconButton
                                       color={'secondary'}
                                       onPressed={() => form.change('amount', remainingBalance?.toString())}
                                   >
                                       <CopyAll className={'icon small'}/>
                                   </IconButton>
                                </span>
                            </div>
                        </Col>
                        <Col xs={12} className={'px-4'}>
                            <OutlinedDropdownForm
                                label={'Method'}
                                name={`paymentMethod`}
                                required={!!required.paymentMethod}
                                data={otherPaymentMethods.map(e => ({
                                    value: e,
                                    label: e.description,
                                    id: e.id
                                })) ?? []}
                            />
                        </Col>
                        <Col xs={12} className={'px-4 mt-4'}>
                            <OutlinedInputForm
                                label={'Amount'}
                                name={'amount'}
                                type={'number'}
                                min={'0'}
                                required={required.amount}
                                value={values?.amount ?? ''}
                                onChange={(e) => form.change(
                                    e.target.name,
                                    !e.target.value
                                        ? undefined
                                        : parseFloat(e.target.value) >= minChargeAmount
                                            ? e.target.value
                                            : minChargeAmount
                                )}
                            />
                        </Col>
                        <Col xs={12} className={'px-4 mt-4'}>
                            <MuiRFF.DatePicker
                                name={"date"}
                                label={"Date"}
                                required={required.date}
                            />
                        </Col>
                        <Col xs={12}
                             className={'px-4 mt-2 row no-gutters justify-content-start'}>
                            <CheckboxForm
                                onChange={(e) => onManualReferenceChange(e, form)}
                                name={'manual'}
                                required={required.manual}
                                data={{
                                    value: values?.manual ?? false,
                                    label: 'Manual Reference Number'
                                }}
                            />
                        </Col>
                        <Col xs={12} className={'px-4 mt-2'}>
                            <Fade in={!!values?.manual} unmountOnExit mountOnEnter>
                                <div className={'w-100'}>
                                    <OutlinedInputForm
                                        disabled={!values?.manual}
                                        label={'Manual Reference'}
                                        name={'reference'}
                                        type={'text'}
                                        required={required.reference}
                                        value={values?.reference ?? ''}
                                        onChange={(e) => form.change(e.target.name, e.target.value)}
                                    />
                                </div>
                            </Fade>
                        </Col>
                        <div className='w-100 my-2'/>
                    </Row>
                }}
            />
        </>
    )
}

export default PayCustomerOrderOtherPaymentsSection;
